import type { Table } from '@tanstack/react-table';
import type { ComponentProps, ReactNode } from 'react';

import { Badge } from '@/components/core/badge';
import { CardTitle } from '@/components/core/card';
import { cn } from '@/shared/helpers';

type DataTableCardTitleProps<TData> = Omit<ComponentProps<typeof CardTitle>, 'children'> & {
  table: Table<TData>;
  rowCount?: number;
  children: ReactNode;
};

export function DataTableCardTitle<TData>({
  table,
  rowCount: rowCountProp,
  className,
  children,
  ...props
}: DataTableCardTitleProps<TData>) {
  const rowCount = rowCountProp ?? table.getRowCount();

  return (
    <CardTitle className={cn('flex items-center gap-2', className)} {...props}>
      <span>{children}</span>
      <Badge variant="secondary" aria-label={`with ${rowCount} rows`}>
        {rowCount}
      </Badge>
    </CardTitle>
  );
}

export function DataTableCardEmpty({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="data-table-card-empty"
      className={cn(
        'flex min-h-15 items-center justify-center bg-background px-4 text-center text-muted-foreground group-data-[size=sm]/card:px-3',
        className,
      )}
      {...props}
    />
  );
}

type DataTableCardBodyProps<TData> = {
  table: Table<TData>;
  emptyMessage?: ReactNode;
  children: ReactNode;
};

export function DataTableCardBody<TData>({
  table,
  emptyMessage = 'No results found',
  children,
}: DataTableCardBodyProps<TData>) {
  const { isLoading, hasError } = table.options.meta ?? {};
  const isEmpty = !isLoading && !hasError && table.getRowCount() === 0;

  if (isEmpty) {
    return emptyMessage === null ? null : <DataTableCardEmpty>{emptyMessage}</DataTableCardEmpty>;
  }

  return (
    <div
      data-slot="data-table-card-body"
      className={cn(
        // If the table is the card's last section, round its actual final row group so
        // card overflow clipping does not cut off cell focus rings at the bottom corners.
        '[&:last-child_[data-slot=table]>:last-child>tr:last-child>*:first-child]:rounded-bl-xl [&:last-child_[data-slot=table]>:last-child>tr:last-child>*:last-child]:rounded-br-xl',
      )}
    >
      {children}
    </div>
  );
}
